import type { UpdateInternalAuditMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateInternalAuditDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

import type {
  UpdateInternalAuditInput,
  UpdateInternalAuditRequest,
} from './types';

export const useUpdateInternalAudit = createMutationHook({
  name: 'updateInternalAudit',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.internalAuditEntity.update.mutationOptions({ onSuccess }),
  mapInput: (
    variables: UpdateInternalAuditInput
  ): UpdateInternalAuditRequest => ({
    Id: variables.Id,
    Title: variables.Title ?? '',
    OriginalTimestamp: variables.OriginalTimestamp,
    Description: variables.Description,
    BusinessArea: variables.BusinessArea,
    BusinessAreaId: variables.BusinessAreaId,
    CustomAttributeData: variables.CustomAttributeData,
    OwnerUserIds: variables.OwnerUserIds ?? [],
    OwnerGroupIds: variables.OwnerGroupIds ?? [],
    ContributorUserIds: variables.ContributorUserIds ?? [],
    ContributorGroupIds: variables.ContributorGroupIds ?? [],
    ViewerUserIds: variables.ViewerUserIds,
    ViewerGroupIds: variables.ViewerGroupIds,
    TagTypeIds: variables.TagTypeIds ?? [],
    DepartmentTypeIds: variables.DepartmentTypeIds ?? [],
  }),
  invalidate: (trpc) => [
    trpc.frontend.internalAuditEntity.register.queryKey(),
    trpc.frontend.internalAuditEntity.internalAuditById.queryKey(),
    // The update find-or-creates a business_area for a new free-text title
    // and runs the org-scoped orphan cleanup server-side, so the
    // business-area list can change either way — refresh it (parity with
    // the Apollo path's evictField(cache, 'business_area')).
    trpc.frontend.businessArea.businessAreas.queryKey(),
    // COI-889: this hook rewrites the owner/contributor sets, whose triggers
    // maintain the grant cache `perm_v2_entity_permissions` reads (60-min
    // window, `refetchOnMount: false`, so it never self-heals).
    trpc.frontend.permission.getEntityPermissions.queryKey(),
  ],
  mapResponse: (result: { Id: string }): UpdateInternalAuditMutation => ({
    updateInternalAudit: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: UpdateInternalAuditDocument,
  graphqlVariables: (variables: UpdateInternalAuditInput) => ({
    Input: variables,
  }),
  evictFields: ['internal_audit_entity', 'business_area'],
});
